import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { normalizeProfileVertical, type ProfileVertical } from '@photofeeler/shared';
import { useUserStore } from '@/stores/user';
import { useGameStore } from '@/stores/game';
import { BottomNav, type TabId } from './BottomNav';
import { Onboarding } from './Onboarding';
import { ScoreTab } from '@/tabs/ScoreTab';
import { BattleTab } from '@/tabs/BattleTab';
import { NewTab } from '@/tabs/NewTab';
import { LearnTab } from '@/tabs/LearnTab';
import { MeTab } from '@/tabs/MeTab';

export function AppShell() {
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const requestedVertical = normalizeProfileVertical(searchParams.get('vertical'));
  const initialTab: TabId =
    requestedTab === 'battle' ||
    requestedTab === 'new' ||
    requestedTab === 'learn' ||
    requestedTab === 'me'
      ? requestedTab
      : 'score';
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const navigate = useNavigate();
  const { credits } = useUserStore();
  const { onboardingComplete } = useGameStore();

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  if (!onboardingComplete) {
    return <Onboarding onComplete={(tab) => setActiveTab(tab)} />;
  }

  return (
    <div className="min-h-screen bg-cream relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[22rem] bg-[radial-gradient(circle_at_top,_rgba(201,104,91,0.16),_transparent_42%)]" />
      <div className="pointer-events-none absolute right-0 top-16 h-[18rem] w-[18rem] rounded-full bg-info/10 blur-3xl" />
      {/* Top Header */}
      <header className="sticky top-0 z-40 px-4 sm:px-6 pt-4">
        <div className="max-w-6xl mx-auto app-shell-panel flex items-center justify-between px-4 sm:px-5 h-[74px]">
          <Link to="/app" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose to-rose-dark text-white flex items-center justify-center shadow-warm">
              <span className="font-serif text-lg" style={{ fontWeight: 700 }}>P</span>
            </div>
            <div>
              <div className="font-serif text-2xl text-ink leading-none" style={{ fontWeight: 600 }}>
                Photo<span className="text-rose">Feeler</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-ink-faint mt-1">
                Profile Workspace
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 hero-chip py-2 px-3">
              <span className="w-2 h-2 rounded-full bg-success" />
              <span className="text-[11px] uppercase tracking-[0.18em] text-ink-faint">
                Dating + LinkedIn
              </span>
            </div>
            <button
              onClick={() => navigate('/purchase')}
              className="hero-chip py-2 px-3 hover:border-ink/15 transition-colors"
            >
              <span className="text-xs font-sans font-medium text-ink">{credits}</span>
              <span className="text-[10px] font-sans text-ink-faint" style={{ fontWeight: 300 }}>
                credits
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Tab Content */}
      <main className="relative">
        {activeTab === 'score' && <ScoreTab />}
        {activeTab === 'battle' && <BattleTab />}
        {activeTab === 'new' && <NewTab initialVertical={requestedVertical as ProfileVertical} />}
        {activeTab === 'learn' && <LearnTab />}
        {activeTab === 'me' && <MeTab />}
      </main>

      {/* Bottom Navigation */}
      <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
}
